import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface SectionRegistryDispatch {
  registerSection: (id: string) => void;
  unregisterSection: (id: string) => void;
}

const SectionRegistryDispatchContext = createContext<SectionRegistryDispatch>({
  registerSection: () => {},
  unregisterSection: () => {},
});

const SectionRegistryStateContext = createContext<Set<string>>(new Set());

export const SectionRegistryProvider = ({ children }: { children: React.ReactNode }) => {
  const [registeredSections, setRegisteredSections] = useState<Set<string>>(new Set());

  const registerSection = useCallback((id: string) => {
    setRegisteredSections((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const unregisterSection = useCallback((id: string) => {
    setRegisteredSections((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const dispatch = useMemo(
    () => ({ registerSection, unregisterSection }),
    [registerSection, unregisterSection]
  );

  return (
    <SectionRegistryDispatchContext.Provider value={dispatch}>
      <SectionRegistryStateContext.Provider value={registeredSections}>
        {children}
      </SectionRegistryStateContext.Provider>
    </SectionRegistryDispatchContext.Provider>
  );
};

export const useSectionRegistryDispatch = () => useContext(SectionRegistryDispatchContext);

export const useSectionRegistryState = () => useContext(SectionRegistryStateContext);
